import React, { useEffect, useState } from "react";
import "tailwindcss/tailwind.css";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { getCustomerNotifications } from "../../store/slices/customerSlice";
import Loading from "../../components/AssetsComponent/loading";
import { Bell, Clock, User } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CustomerNotificationsPage: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();
  const navigate = useNavigate();
  const [filterDays, setFilterDays] = useState<"all" | 15 | 30>("all");

  useEffect(() => {
    dispatch(getCustomerNotifications(15));
    dispatch(getCustomerNotifications(30));
  }, [dispatch]);

  const notifications15 = useSelector(
    (state: RootState) => state.customer.notifications[15] || []
  );
  const notifications30 = useSelector(
    (state: RootState) => state.customer.notifications[30] || []
  );

  let filteredNotifications: { data: any[]; label: string; color: string }[] =
    [];

  if (filterDays === 15) {
    filteredNotifications = [
      {
        data: notifications15,
        label: "15 days",
        color: "border-yellow-500 text-yellow-500",
      },
    ];
  } else if (filterDays === 30) {
    filteredNotifications = [
      {
        data: notifications30,
        label: "30+ days",
        color: "border-red-600 text-red-600",
      },
    ];
  } else {
    filteredNotifications = [
      {
        data: notifications15,
        label: "15 days",
        color: "border-yellow-500 text-yellow-500",
      },
      {
        data: notifications30,
        label: "30+ days",
        color: "border-red-600 text-red-600",
      },
    ];
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <div className="bg-white shadow-lg rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center">
            <span className="w-1 h-6 bg-navyBlue-primary mr-3 rounded-full" />
            Customer Order Notifications
          </h2>

          <select
            value={filterDays}
            onChange={(e) =>
              setFilterDays(
                e.target.value === "all"
                  ? "all"
                  : (parseInt(e.target.value, 10) as 15 | 30)
              )
            }
            className="border rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All</option>
            <option value="15">15 Days</option>
            <option value="30">30 Days</option>
          </select>
        </div>

        {filteredNotifications.map(({ data, label, color }) => (
          <div key={label} className="mb-6">
            <h3 className="text-md font-medium text-gray-700 mb-2">
              inactive for {label}
            </h3>
            {data.length === 0 ? (
              <p className="text-gray-600 flex items-center gap-2">
                <Bell className="w-5 h-5 text-blue-600" />
                No notifications
              </p>
            ) : (
              <ul className="space-y-4">
                {data.map((cust) => (
                  <li
                    key={cust._id}
                    className={`p-4 bg-white rounded-lg shadow-md border-l-4 ${
                      color.split(" ")[0]
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <User className={`w-5 h-5 ${color.split(" ")[1]}`} />
                      <p className="text-gray-800">
                        {cust.name || cust.contactPersonName} has not placed an
                        order in the last {label}.
                      </p>
                      {cust.lastCalled && (
                        <div className="flex items-center">
                          <span className="flex items-center gap-2 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                            <Clock className="w-4 h-4 text-slate-400" />
                            Last contacted {cust.lastCalled} days ago
                          </span>
                        </div>
                      )}
                      <a
                        href={`/lead-details/${cust?.leadId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto text-xs font-medium text-blue-600 hover:underline"
                      >
                        View Full Lead Profile →
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomerNotificationsPage;
